import { Box } from "@mui/material";
import { grey } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isFunction, isNil, size } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import { loanFileStatusColumnDef } from "../agGrid/cellRenderer/LoanFileStatusCell";
import Flx from "../layout/Flx";
import LoanFilePreviewModal from "../modals/files/LoanFilePreviewModal";
import Txt from "../typography/Txt";

const getExtStyle = (ext = "") => {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return { bg: "#FCEBEB", fg: "#791F1F", border: "#791f1f20" };
  if (["jpg", "jpeg", "png"].includes(e))
    return { bg: "#E6F1FB", fg: "#0C447C", border: "#0C447C20" };
  if (["doc", "docx"].includes(e))
    return { bg: "#E1F5EE", fg: "#085041", border: "#08504120" };
  if (["xls", "xlsx", "csv"].includes(e))
    return { bg: "#EAF3DE", fg: "#27500A", border: "#27500A20" };
  return { bg: "#F1EFE8", fg: "#444441", border: "#44444120" };
};

// =================================================================
// CELL RENDERERS
// =================================================================

const FilenameCell = ({ doc }) => {
  if (!doc) return null;
  const extStyle = getExtStyle(doc.file_extension);
  return (
    <Flx ac g={1.25} sx={{ minWidth: 0, height: "100%" }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "9px",
          fontWeight: 600,
          letterSpacing: "0.3px",
          flexShrink: 0,
          bgcolor: extStyle.bg,
          color: extStyle.fg,
          border: `1px solid ${extStyle.border}`,
        }}
      >
        {(doc.file_extension || "FILE").toUpperCase()}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Txt
          sx={{
            display: "block",
            fontSize: "11px",
            // fontSize: "13px",
            fontWeight: 500,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            "&:hover": { color: "#185FA5", textDecoration: "underline" },
          }}
        >
          {doc.file_display_name}
        </Txt>
        <Txt
          sx={{
            display: "block",
            fontSize: "9.5px",
            color: grey[600],
            fontStyle: isNil(doc.docType) ? "italic" : "normal",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            mt: "1px",
          }}
        >
          {doc.docType || "Uncategorized"}
        </Txt>
      </Box>
    </Flx>
  );
};

// =================================================================
// MAIN COMPONENT
// =================================================================

const LoanFilesCompactTable = ({
  files,
  emptyMessage,
  showStatus = true,
  quickFilterText,
  relatedFiles,
  hideIfEmpty,
  onCellClicked,
  sx,
}) => {
  const dispatch = useDispatch();
  const [file, setFile] = useState(null);
  const columnDefs = useMemo(
    () => [
      {
        colId: "file_display_name",
        headerName: "Filename",
        field: "file_display_name",
        flex: 2,
        sort: "asc",
        minWidth: 240,
        autoHeight: true,
        cellStyle: { cursor: "pointer", paddingTop: 8, paddingBottom: 8 },
        cellRenderer: (params) => <FilenameCell doc={params.data} />,
      },
      loanFileStatusColumnDef({ hide: !showStatus }),
    ],
    [showStatus],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      cellClass: "ag-cell-vertical-center",
    }),
    [],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const handleOnCellClicked = useCallback(
    (params) => {
      if (params.node.group) return;
      if (!params.data) return;

      if (isFunction(onCellClicked)) {
        onCellClicked(params?.data);
      }
    },
    [onCellClicked],
  );

  const onRowClicked = useCallback((params) => {
    const clickedElement = params.event?.target;
    // console.log("params.event?.target", params);
    if (
      (clickedElement instanceof Element && clickedElement.closest("button")) ||
      (clickedElement instanceof Element && clickedElement.closest("a"))
    ) {
      return;
    }
    if (params.node.group) {
      params.node.setExpanded(!params.node.expanded);
      return;
    }
    setFile(params?.data);
  }, []);

  if (hideIfEmpty && isEmpty(files)) return null;
  if (emptyMessage && isEmpty(files)) {
    return (
      <Txt secondary sx={{ p: 1 }}>
        {emptyMessage}
      </Txt>
    );
  }

  return (
    <>
      <AgGridTableWrapper
        compactTable
        noBorder
        borderRadius={0}
        suppressMinHeightWhenPopulated={files}
        sx={{
          "&.ag-theme-quartz.compact-table .ag-root-wrapper": {
            borderRadius: "0px !important",
          },
          ...sx,
        }}
      >
        <AgGridReact
          rowData={files}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          onCellClicked={handleOnCellClicked}
          quickFilterText={quickFilterText}
          domLayout="autoHeight"
          onRowClicked={onRowClicked}
          tooltipShowDelay={100}
          headerHeight={0}
          enableCellTextSelection
          suppressCellFocus
        />
      </AgGridTableWrapper>
      <LoanFilePreviewModal
        file={file}
        relatedFiles={size(files) > 1 ? files : null}
        open={!isNil(file)}
        containerId={file?.containerName}
        onClose={() => setFile(null)}
        onSelectRelatedFile={setFile}
      />
    </>
  );
};

export default LoanFilesCompactTable;
